import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, GraduationCap, CalendarCheck, XCircle, Megaphone, HeartHandshake, QrCode, Smartphone, Monitor, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { STAT_ART, portraitFor } from "@/constants/images";
import SearchFrequencyPanel from "@/components/SearchFrequencyPanel";
import WeeklyComparisonWidget from "@/components/WeeklyComparisonWidget";

function StatCard({ icon: Icon, label, value, color, testid, art }) {
  return (
    <Card data-testid={testid} className="card-elevated border-slate-200/70 relative overflow-hidden group">
      {art && (
        <img
          src={art}
          alt=""
          className="absolute inset-y-0 left-0 w-1/2 h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity"
          style={{ maskImage: "linear-gradient(to right, transparent, black 60%)", WebkitMaskImage: "linear-gradient(to right, transparent, black 60%)" }}
        />
      )}
      <CardContent className="p-6 flex items-center gap-4 relative">
        <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center shrink-0 ring-4 ring-white`}>
          <Icon className="w-7 h-7" />
        </div>
        <div>
          <div className="text-4xl font-black text-slate-900 leading-none">{value}</div>
          <div className="text-sm text-slate-500 mt-1 font-semibold">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [children, setChildren] = useState([]);
  const [scanStats, setScanStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      if (user.role === "admin") {
        const { data } = await api.get("/stats/overview");
        setStats(data);
        try {
          const ss = await api.get("/admin/cert-scan-stats");
          setScanStats(ss.data);
        } catch { /* optional */ }
      }
      const [ann, ch] = await Promise.all([
        api.get("/announcements"),
        api.get("/children"),
      ]);
      setAnnouncements(ann.data);
      setChildren(ch.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }, [user.role]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh when tab regains focus / becomes visible again
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") loadAll(); };
    window.addEventListener("focus", loadAll);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", loadAll);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadAll]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-bold uppercase tracking-widest text-sky-700 mb-2">أهلاً بك</div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">مرحباً، {user.name}</h1>
          <p className="text-slate-500 mt-2 text-base md:text-lg">
            {user.role === "admin" && "نظرة عامة على المركز اليوم"}
            {user.role === "teacher" && "أطفالك ومهامك لهذا اليوم"}
            {user.role === "parent" && "متابعة يوم طفلك ورسائل المعلم"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadAll}
            disabled={refreshing}
            data-testid="dashboard-refresh-btn"
            className="gap-2"
            title={lastRefresh ? `آخر تحديث: ${lastRefresh.toLocaleTimeString("ar-EG")}` : "تحديث"}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            تحديث
          </Button>
          <div className="hidden md:flex items-center gap-3 bg-white rounded-2xl border border-slate-200 px-5 py-3">
            <HeartHandshake className="w-6 h-6 text-sky-600" />
            <div>
              <div className="text-xs text-slate-500">اليوم</div>
              <div className="font-bold text-slate-900">
                {new Date().toLocaleDateString("ar-EG", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {user.role === "admin" && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard testid="stat-children" icon={Users} label="عدد الطلاب" value={stats.children_count} color="bg-sky-100 text-sky-700" art={STAT_ART.children} />
          <StatCard testid="stat-teachers" icon={GraduationCap} label="المعلمون والمعالجون" value={stats.teachers_count} color="bg-emerald-100 text-emerald-700" art={STAT_ART.teachers} />
          <StatCard testid="stat-present" icon={CalendarCheck} label="حاضرون اليوم" value={stats.present_today} color="bg-emerald-100 text-emerald-700" art={STAT_ART.present} />
          <StatCard testid="stat-absent" icon={XCircle} label="غائبون اليوم" value={stats.absent_today} color="bg-rose-100 text-rose-700" art={STAT_ART.absent} />
        </div>
      )}

      {user.role === "admin" && <WeeklyComparisonWidget />}
      {user.role === "admin" && <SearchFrequencyPanel />}

      {user.role === "admin" && scanStats && scanStats.total > 0 && (
        <Card className="border-slate-200/70 card-elevated" data-testid="cert-scan-analytics">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 text-white flex items-center justify-center shadow-md">
                  <QrCode className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-lg font-black text-slate-900">مسح شهادات (Social Proof)</div>
                  <div className="text-xs text-slate-500">كل مرة يمسح فيها ولي أمر رمز QR على شهادة الطفل يظهر هنا</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center px-4 py-2 rounded-xl bg-amber-50 border border-amber-200" data-testid="cert-scan-total">
                  <div className="text-2xl font-black text-amber-700">{scanStats.total}</div>
                  <div className="text-[10px] text-amber-800 font-bold">إجمالي</div>
                </div>
                <div className="text-center px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="cert-scan-last-7d">
                  <div className="text-2xl font-black text-emerald-700">{scanStats.last_7d}</div>
                  <div className="text-[10px] text-emerald-800 font-bold">آخر 7 أيام</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="text-xs font-black text-slate-700 mb-2">🏆 الأكثر مشاركة</div>
                {scanStats.top_children?.length === 0 ? (
                  <div className="text-xs text-slate-400">لم تُسجَّل عمليات مسح بعد</div>
                ) : (
                  <div className="space-y-1.5">
                    {scanStats.top_children.map((c, i) => (
                      <div key={c.child_id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50" data-testid={`top-scan-${c.child_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-black flex items-center justify-center shrink-0">{i + 1}</span>
                          <span className="font-bold text-sm text-slate-900 truncate">{c.child_name || "—"}</span>
                        </div>
                        <span className="text-xs font-black text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">{c.count} مسح</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-black text-slate-700 mb-2">🕐 آخر عمليات المسح</div>
                {scanStats.recent?.length === 0 ? (
                  <div className="text-xs text-slate-400">لا يوجد سجل بعد</div>
                ) : (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                    {scanStats.recent.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 text-xs">
                        {r.device === "mobile" ? <Smartphone className="w-3.5 h-3.5 text-slate-500 shrink-0" /> : <Monitor className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                        <span className="font-bold text-slate-900 flex-1 truncate">{r.child_name || "—"}</span>
                        <span className="text-slate-400 shrink-0">{new Date(r.scanned_at).toLocaleString("ar-EG", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200/70 card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {user.role === "parent" ? "طالبي" : "الطلاب"}
              </h2>
              <Link to="/children" className="text-sky-700 font-bold text-sm hover:underline" data-testid="view-all-children">
                عرض الكل ←
              </Link>
            </div>
            {children.length === 0 ? (
              <EmptyState label="لا يوجد أطفال بعد" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {children.slice(0, 6).map((c) => (
                  <Link
                    key={c.id}
                    to={`/children`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 hover:border-sky-200 border border-transparent transition-all"
                  >
                    <img src={portraitFor(c.id)} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm" />
                    <div>
                      <div className="font-bold text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.disability_type}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-sky-700" />
              <h2 className="text-xl font-black text-slate-900">آخر الإعلانات</h2>
            </div>
            {announcements.length === 0 ? (
              <EmptyState label="لا توجد إعلانات" />
            ) : (
              <div className="space-y-3">
                {announcements.slice(0, 4).map((a) => (
                  <div key={a.id} className="p-3 rounded-xl bg-sky-50/60 border border-sky-100">
                    <div className="font-bold text-slate-900 text-sm">{a.title}</div>
                    <div className="text-xs text-slate-600 line-clamp-2 mt-1">{a.body}</div>
                    <div className="text-[10px] text-slate-400 mt-2">{new Date(a.timestamp).toLocaleString("ar-EG")}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-10 text-center text-slate-400 text-sm">{label}</div>
  );
}
